import { CiaoWorker } from "../core/js/ciao-prolog";

export type cp_query = {
    name: string,
    args: cp_term[]
}

export type QueryResponse = {
    cont: string;
    arg: string;
}

// Term - Atm, Num, String, Struct
export type cp_term =
    | cp_atm
    | cp_num
    | cp_string
    | cp_struct;

// Integer
export type cp_int = {
    tag: 'int';
    value: number;
};

// Float
export type cp_flt = {
    tag: 'flt';
    value: number;
};

// Number
export type cp_num = cp_int | cp_flt;

// Struct
export type cp_struct = {
    tag: 'struct';
    value: object;
};

// Atom
export type cp_atm = {
    tag: 'atm';
    value: string;
};

// String
export type cp_string = {
    tag: 'string';
    value: string;
};

export type CiaoConfig = {
    boot_path: string;
    curr_dir: string;
    init_bundles: string[];
    cust_modules: string[];
    cust_modules_path: string;
    init_queries: string[];
}

export class CiaoPrologInterface {
    private c_config: CiaoConfig;
    private c_worker: CiaoWorker;

    constructor(curr_dir: string, bundles: string[], cust_modules: string[] = [], cust_modules_path?: string, boot_path?: string) {
        this.c_config = {
            boot_path: boot_path ?? './node_modules/@ciao-lang/ts-ciao-interface/dist/core/ciao/',
            curr_dir: curr_dir,
            init_bundles: bundles,
            cust_modules: cust_modules,
            cust_modules_path: cust_modules_path ?? 'src/ciao-modules/',
            init_queries: [
                'use_module(engine(internals), [reload_bundleregs/0])',
                'use_module(library(classic/classic_predicates))'
            ]
        };

        this.c_worker = new CiaoWorker(this.c_config.boot_path);
    }

    async ciao_init(dstdir: string): Promise<void> {
        this.c_worker = new CiaoWorker(this.c_config.boot_path);
        await this.c_worker.ensure_init(1); // ensure init
        for (const bundle of this.c_config.init_bundles) await this.c_worker.use_bundle(bundle); // use bundles
        for (const query of this.c_config.init_queries) await this.c_worker.query_one_begin(query); // init queries

        // Mount current directory as dstdir
        this.c_worker.llciao.mount_dir(this.c_config.curr_dir, dstdir);
        await this.c_worker.query_one_begin(`working_directory(_,'${dstdir}')`);

        // Use custom modules
        for (const cust_module of this.c_config.cust_modules) await this.c_worker.query_one_begin(`use_module('${this.c_config.cust_modules_path}${cust_module}.pl')`); // use modules

        console.log('Ciao worker initiated');
    }

    /* Helper functions for term creation */
    ciao_mk_int(value: number): cp_int {
        return this.ciao_term('int', value) as cp_int;
    }
    ciao_mk_flt(value: number): cp_flt {
        return this.ciao_term('flt', value) as cp_flt;
    }
    ciao_mk_atm(value: string): cp_term {
        return this.ciao_term('atm', value);
    }
    ciao_mk_string(value: string): cp_string {
        return this.ciao_term('string', value) as cp_string;
    }
    ciao_mk_struct(name: string, args: cp_term[]): cp_term {
        const value = { name: name }
        for (let i = 0; i < args.length; i++) (value as any)[i] = args[i]
        return this.ciao_term('struct', value);
    }
    ciao_mk_list<T>(items: T[]): cp_term {
        return this.ciao_term('string', `[${items.toString()}]`);
    }
    ciao_mk_list_of_lists<T>(lists: T[][]): cp_term {
        return this.ciao_term('string', `[${lists.map(list => this.ciao_mk_list<T>(list).value).toString()}]`);
    }
    ciao_mk_reg_type(type: cp_term['tag'], value: any): cp_term {
        return this.ciao_term(type, value);
    }
    private ciao_term(type: cp_term['tag'], value: any): cp_term {
        return { tag: type, value: value }
    }

    /* Term navigation */
    ciao_get_int(term: cp_int): number {
        return term.value;
    }
    ciao_get_flt(term: cp_flt): number {
        return term.value;
    }
    ciao_get_atm(term: cp_atm): string {
        return term.value;
    }
    ciao_get_string(term: cp_string): string {
        return term.value;
    }
    ciao_get_struct(term: cp_struct): object {
        return term.value;
    }
    ciao_get_list_from_string<T>(term: cp_string): T[] {
        // TODO - Only works for number list - JSON communication
        const array_start = term.value.indexOf('[');
        const array = term.value.substring(array_start, term.value.length);
        return JSON.parse(array);
    }
    ciao_get_list_of_lists_from_string<T>(term: cp_string): T[][] {
        // TODO - Only works for number list - JSON communication
        const array_start = term.value.indexOf('[');
        const array = term.value.substring(array_start, term.value.length);
        return JSON.parse(array);
    }
    ciao_get_reg_type(term: cp_term): any {
        return term.value;
    }

    /* Term checks */
    ciao_is_int(term: cp_term): boolean {
        return this.ciao_is_type(term, 'int');
    }
    ciao_is_flt(term: cp_term): boolean {
        return this.ciao_is_type(term, 'flt');
    }
    ciao_is_struct(term: cp_term): boolean {
        return this.ciao_is_type(term, 'struct');
    }
    ciao_is_atm(term: cp_term): boolean {
        return this.ciao_is_type(term, 'atm');
    }
    ciao_is_string(term: cp_term): boolean {
        return this.ciao_is_type(term, 'string');
    }
    ciao_is_type(term: cp_term, type: string): boolean {
        return term.tag === type;
    }


    /* Queries and calls */
    async ciao_query_begin(query: cp_query): Promise<QueryResponse> {
        const arg_values = query.args.map((term) => term.value); // getting values
        const q: string = `${query.name}(${arg_values})`; // structuring query
        return await this.c_worker.query_one_begin(q); // sending query
    }

    async ciao_query_next(): Promise<QueryResponse> {
        return await this.c_worker.query_one_next(); // getting next solution
    }

    async ciao_query_end(): Promise<void> {
        return await this.c_worker.query_end(); // ending query
    }
}